import { NEEDS, INTERPRETATION } from "@/data/needs";

const L = (lang, fr, es) => (lang === "es" ? es : fr);

export function needSatisfaction(satisfaction, key) {
  const arr = satisfaction[key] || [];
  return arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0); // /20
}

export function interpretationFor(score, lang = "fr") {
  const r = INTERPRETATION.find((x) => score >= x.min && score <= x.max) || INTERPRETATION[0];
  return { min: r.min, max: r.max, color: r.color, label: r[lang] };
}

export function ecartLabel(ecart, lang = "fr") {
  const a = Math.abs(ecart);
  if (a <= 2) return { label: L(lang, "Perception juste", "Percepción ajustada"), color: "#8A9A6D" };
  if (ecart > 0) return { label: L(lang, "Surestimé", "Sobrestimado"), color: "#E99F4F" };
  return { label: L(lang, "Sous-estimé", "Subestimado"), color: "#B0553B" };
}

// satisfaction: { needKey: [i0..i4] } (mesurée, /20)
// estimated: { needKey: 0..20 } (auto-évaluation globale)
export function computeResults(satisfaction, estimated, lang = "fr") {
  const rows = NEEDS.map((need) => {
    const measured = needSatisfaction(satisfaction, need.key); // /20
    const est = estimated[need.key] ?? 0; // /20
    const ecart = est - measured; // positif = surestimation
    return {
      key: need.key,
      name: need.name[lang],
      short: need.short[lang],
      measured,
      estimated: est,
      ecart,
      ecartInfo: ecartLabel(ecart, lang),
      interpretation: interpretationFor(measured, lang),
    };
  });

  const bySatDesc = [...rows].sort((a, b) => b.measured - a.measured);
  const bySatAsc = [...rows].sort((a, b) => a.measured - b.measured);
  const overestimated = [...rows].filter((r) => r.ecart > 2).sort((a, b) => b.ecart - a.ecart);
  const underestimated = [...rows].filter((r) => r.ecart < -2).sort((a, b) => a.ecart - b.ecart);
  const byEcartAbs = [...rows].sort((a, b) => Math.abs(b.ecart) - Math.abs(a.ecart));

  return {
    rows,
    topResources: bySatDesc.slice(0, 3),
    fragile: bySatAsc.slice(0, 3),
    overestimated: overestimated.slice(0, 3),
    underestimated: underestimated.slice(0, 3),
    byEcartAbs,
  };
}

function formatList(arr, lang) {
  const names = arr.map((r) => r.short.toLowerCase());
  if (names.length <= 1) return names[0] || "";
  const sep = L(lang, " et ", " y ");
  return names.slice(0, -1).join(", ") + sep + names[names.length - 1];
}

export function clinicalSynthesis(results, lang = "fr") {
  const { topResources, fragile, overestimated, underestimated } = results;
  const parts = [];

  const strong = topResources.filter((r) => r.measured >= 11);
  if (strong.length) {
    const solid = strong.some((r) => r.measured >= 16);
    parts.push(
      L(
        lang,
        `Votre profil met en évidence des ressources ${solid ? "solides" : "présentes"} dans les domaines du ${formatList(strong, lang)}.`,
        `Tu perfil pone de manifiesto recursos ${solid ? "sólidos" : "presentes"} en los ámbitos de ${formatList(strong, lang)}.`
      )
    );
  } else {
    parts.push(
      L(
        lang,
        "Votre profil met en évidence un niveau de satisfaction globalement modéré sur l'ensemble des besoins évalués.",
        "Tu perfil muestra un nivel de satisfacción globalmente moderado en el conjunto de las necesidades evaluadas."
      )
    );
  }

  const frag = fragile.filter((r) => r.measured <= 10);
  if (frag.length) {
    parts.push(
      L(
        lang,
        `En revanche, ${frag.length > 1 ? "les besoins" : "le besoin"} de ${formatList(frag, lang)} ${frag.length > 1 ? "apparaissent" : "apparaît"} moins satisfait${frag.length > 1 ? "s" : ""}.`,
        `En cambio, ${frag.length > 1 ? "las necesidades" : "la necesidad"} de ${formatList(frag, lang)} ${frag.length > 1 ? "aparecen" : "aparece"} menos satisfecha${frag.length > 1 ? "s" : ""}.`
      )
    );
  }

  if (overestimated.length) {
    parts.push(
      L(
        lang,
        `Vous semblez surestimer votre satisfaction pour ${formatList(overestimated, lang)} : votre ressenti global y est plus favorable que ce que révèle le détail des items — un point de vigilance à explorer.`,
        `Pareces sobrestimar tu satisfacción en ${formatList(overestimated, lang)}: tu sensación global es más favorable de lo que revela el detalle de los ítems — un punto de vigilancia a explorar.`
      )
    );
  }
  if (underestimated.length) {
    parts.push(
      L(
        lang,
        `À l'inverse, ${formatList(underestimated, lang)} ${underestimated.length > 1 ? "sont sous-estimés" : "est sous-estimé"} : ${underestimated.length > 1 ? "ces besoins sont" : "ce besoin est"} en réalité mieux satisfait${underestimated.length > 1 ? "s" : ""} que votre perception globale.`,
        `Por el contrario, ${formatList(underestimated, lang)} ${underestimated.length > 1 ? "están subestimadas" : "está subestimada"}: en realidad ${underestimated.length > 1 ? "estas necesidades están" : "esta necesidad está"} mejor satisfecha${underestimated.length > 1 ? "s" : ""} que tu percepción global.`
      )
    );
  }
  if (!overestimated.length && !underestimated.length) {
    parts.push(
      L(
        lang,
        "Votre auto-évaluation globale est cohérente avec la satisfaction mesurée : votre perception de vos besoins est juste.",
        "Tu autoevaluación global es coherente con la satisfacción medida: tu percepción de tus necesidades es ajustada."
      )
    );
  }

  return parts.join(" ");
}
